import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, CircleHelp, LifeBuoy, LogOut, Plus, Shield, UserRound, Wallet, Zap } from 'lucide-react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT, KIOSK_NAV_APPEAR_SESSION_KEY } from '@/services/apiService';
import { playClickSound, playSound, isSoundEnabled, SOUND_NAMES } from '@/utils/soundUtils';
import { navigationFromState } from '@/utils/navigateBack';
import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import { useAuthModal } from '@/contexts/AuthModalContext';
import KioskNotificationBell from '@/components/KioskNotificationBell';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Button, TopTabs, cx } from '@/components/ui';
import SignupBonusBadge from '@/features/bonus/SignupBonusBadge';
import { useTranslation } from 'react-i18next';
import { useCountUp } from '@/hooks/useCountUp';
import { ANIMATION_REGISTRY, getSharedShineClockStyle } from '@/config/animationRegistry';
import useLogout from '../useLogout';
import { fsClass, wfBrandStyle } from '@/styles/typography';

export interface HeaderProps {
  /** @deprecated Prototype chrome is static — back/breadcrumb ignored. */
  back?: boolean;
  /** @deprecated ignored */
  backTo?: string;
  type: 'logged' | 'normal';
  size?: 'xs' | 'm' | 'l' | 'XL' | 's';
  /** When true, header fits compact chrome height (`--wf-header-h`). Used on Home / fixed shells. */
  compact?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  /** Show account menu chip in right section. Default true. */
  showUserInfo?: boolean;
  /** Show balance box in right section (logged only). Default true. */
  showBalance?: boolean;
  /** Optional custom right slot (e.g. EDIT button). When set, replaces Balance in right section. */
  rightSlot?: React.ReactNode;
  /** When true with type normal, show admin broadcast bell (e.g. logged-in user on home). Logged type always shows the bell. */
  showNotificationBell?: boolean;
}

/** Cosmos top-nav tabs. Games/Wallet/Activity are auth-gated via requireAuth. */
const NAV_TAB_ROUTES: Record<string, string> = {
  home: '/',
  games: '/account/games',
  wallet: '/account/finance/wallet',
  activity: '/account/transactions',
};

function getActiveTabId(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/account/games')) return 'games';
  if (pathname.startsWith('/account/transactions')) return 'activity';
  if (pathname.startsWith('/account/finance') || pathname.startsWith('/account/wallet')) return 'wallet';
  return '';
}

/**
 * Unified Cosmos header shell: dark glass bar with brand mark, top tabs
 * (HOME / GAMES / WALLET / ACTIVITY with beam underline) and account actions.
 * Supports both logged and normal (public) types.
 */
export const Header: React.FC<HeaderProps & React.HTMLAttributes<HTMLElement>> = ({
  back: _back,
  backTo: _backTo,
  type = 'normal',
  size: _size = 'XL',
  compact = false,
  className = '',
  title: _title,
  subtitle: _subtitle,
  showUserInfo = true,
  showBalance = true,
  rightSlot,
  showNotificationBell = false,
  style,
  ...restProps
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, requireAuth, openAuthModal } = useAuthModal();
  const logout = useLogout();

  const showBell = type === 'logged' || showNotificationBell;
  // Balance/account UI applies to logged shells and to authed users on the public shell
  const authedUi = type === 'logged' || isAuthenticated;
  const showBalanceUi = showBalance && authedUi;

  const [balance, setBalance] = useState<number>(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceDelta, setBalanceDelta] = useState<number | null>(null);
  const [showBalanceAttention, setShowBalanceAttention] = useState(false);
  /** Count-up display for the balance chip — smooths abrupt value changes */
  const animatedBalance = useCountUp(balance, ANIMATION_REGISTRY.countUp.headerBalanceMs);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const avatarButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);
  const [menuLayout, setMenuLayout] = useState<{ top: number; left: number; minWidth: number } | null>(
    null
  );
  const previousBalanceRef = useRef<number | null>(null);
  const balanceAttentionTimeoutRef = useRef<number | null>(null);
  const lastBalanceSoundAtRef = useRef<number>(0);
  const [navAppearGen, setNavAppearGen] = useState(0);
  const prevNavTabCountRef = useRef<number | null>(null);
  const prevAuthedRef = useRef(isAuthenticated);
  const navAppearTriggeredRef = useRef(0);
  const loginAppearDoneRef = useRef(false);

  /** Phase-lock Sign in gleam to the same wall clock as Home grid shine. */
  const guestAuthShineStyle = useMemo(
    () => getSharedShineClockStyle() as React.CSSProperties,
    // Recapture phase when the guest CTA mounts again (Header stays mounted across auth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  );

  const triggerNavAppear = useCallback(() => {
    setNavAppearGen((gen) => {
      const next = gen + 1;
      navAppearTriggeredRef.current = next;
      return next;
    });
    sessionStorage.removeItem(KIOSK_NAV_APPEAR_SESSION_KEY);
  }, []);

  const handleNavAppearComplete = useCallback(() => {
    sessionStorage.removeItem(KIOSK_NAV_APPEAR_SESSION_KEY);
  }, []);

  const applyBalanceWithAttention = (nextBalance: number) => {
    const previous = previousBalanceRef.current;
    setBalance(nextBalance);
    previousBalanceRef.current = nextBalance;

    if (previous === null) return;
    const delta = Number((nextBalance - previous).toFixed(2));
    if (Math.abs(delta) < 0.01) return;

    setBalanceDelta(delta);
    setShowBalanceAttention(true);
    if (balanceAttentionTimeoutRef.current) {
      window.clearTimeout(balanceAttentionTimeoutRef.current);
    }
    balanceAttentionTimeoutRef.current = window.setTimeout(() => {
      setShowBalanceAttention(false);
      setBalanceDelta(null);
    }, 2200);

    const now = Date.now();
    if (isSoundEnabled() && now - lastBalanceSoundAtRef.current > 2500) {
      playSound(delta > 0 ? SOUND_NAMES.MONEY_UP : SOUND_NAMES.MONEY_DOWN);
      lastBalanceSoundAtRef.current = now;
    }
  };

  // Load balance when the pill is visible; re-fetch on account switch (token change).
  useEffect(() => {
    if (!showBalanceUi) {
      setBalance(0);
      setBalanceLoading(false);
      previousBalanceRef.current = null;
      setShowBalanceAttention(false);
      setBalanceDelta(null);
      return;
    }

    let cancelled = false;

    const resetLocalBalance = () => {
      previousBalanceRef.current = null;
      setShowBalanceAttention(false);
      setBalanceDelta(null);
      setBalance(0);
    };

    const loadBalance = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true, true);

        if (cancelled) return;

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        if (!cancelled) console.error('Error loading balance:', error);
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };

    void loadBalance();

    const onAuthTokenChanged = () => {
      if (!apiService.isAuthenticated()) {
        resetLocalBalance();
        setBalanceLoading(false);
        return;
      }
      // Drop prior account's painted balance immediately, then force-fetch.
      resetLocalBalance();
      void loadBalance();
    };

    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuthTokenChanged);
    };
  }, [showBalanceUi]);

  // When another part of the app refreshes balance (e.g. Wallet page), update header balance too
  useEffect(() => {
    if (!showBalanceUi) return;

    const onBalanceShouldRefresh = async () => {
      try {
        setBalanceLoading(true);
        const response = await apiService.get<{
          balance: number;
          currency: string;
          lastUpdated: string;
        }>('/payment/balance', true, true);

        if (response.success && response.data) {
          const data = response.data as any;
          const balanceValue = data.balance ?? data.data?.balance ?? 0;
          applyBalanceWithAttention(balanceValue);
        }
      } catch (error) {
        console.error('Error refreshing header balance:', error);
      } finally {
        setBalanceLoading(false);
      }
    };

    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
    return () => window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceShouldRefresh);
  }, [showBalanceUi]);

  const handleReloadBalance = async () => {
    playClickSound();
    try {
      setBalanceLoading(true);
      const response = await apiService.get<{
        balance: number;
        currency: string;
        lastUpdated: string;
      }>('/payment/balance', true, true);

      if (response.success && response.data) {
        const data = response.data as any;
        const balanceValue = data.balance ?? data.data?.balance ?? 0;
        applyBalanceWithAttention(balanceValue);
      }
    } catch (error) {
      console.error('Error reloading balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (balanceAttentionTimeoutRef.current) {
        window.clearTimeout(balanceAttentionTimeoutRef.current);
      }
    };
  }, []);

  const computeAccountMenuLayout = useCallback(() => {
    const trigger = avatarButtonRef.current;
    if (!trigger) return null;
    const rect = trigger.getBoundingClientRect();
    const minWidth = 210;
    const margin = 8;
    const gap = 8;
    const top = rect.bottom + gap;
    let left = rect.right - minWidth;
    left = Math.min(Math.max(margin, left), window.innerWidth - minWidth - margin);
    return { top, left, minWidth };
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuLayout(null);
      return;
    }
    const update = () => {
      const layout = computeAccountMenuLayout();
      if (layout) setMenuLayout(layout);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [menuOpen, computeAccountMenuLayout]);

  // Close account menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || menuPanelRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleTabChange = useCallback(
    (id: string) => {
      playClickSound();
      const route = NAV_TAB_ROUTES[id];
      if (!route) return;
      // Already on this tab's route — don't push history / remount page animations.
      if (location.pathname === route) return;
      if (id === 'home') {
        navigate(route);
        return;
      }
      // Games / Wallet / Activity are auth-gated for guests
      requireAuth(() => navigate(route));
    },
    [navigate, requireAuth, location.pathname]
  );

  const handleMenuNavigate = (path: string) => {
    playClickSound();
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  const handleWalletClick = () => {
    playClickSound();
    if (type === 'normal') {
      sessionStorage.setItem('login_redirect_to', '/account');
      navigate('/login', { state: { redirectTo: '/account' } });
    } else {
      navigate('/account/finance/wallet');
    }
  };

  // Guest Home (Prototype): brand + Home only. Authed: full HOME/GAMES/WALLET/ACTIVITY.
  const navTabs = isAuthenticated
    ? [
        { id: 'home', label: t('header.tabs.home', 'Home'), tourId: 'post-login-tour.home' },
        {
          id: 'games',
          label: t('header.tabs.games', 'Games'),
          tourId: 'post-login-tour.game_management',
        },
        {
          id: 'wallet',
          label: t('header.tabs.wallet', 'Wallet'),
          tourId: 'post-login-tour.wallet',
        },
        {
          id: 'activity',
          label: t('header.tabs.activity', 'Activity'),
          tourId: 'post-login-tour.transactions',
        },
      ]
    : [{ id: 'home', label: t('header.tabs.home', 'Home') }];
  const activeTabId = getActiveTabId(location.pathname);

  useLayoutEffect(() => {
    if (!isAuthenticated) {
      loginAppearDoneRef.current = false;
      prevAuthedRef.current = false;
      prevNavTabCountRef.current = navTabs.length;
      return;
    }

    const prevCount = prevNavTabCountRef.current;
    const expanded = navTabs.length >= 4 && prevCount !== null && prevCount < 4;
    const justAuthed = !prevAuthedRef.current && isAuthenticated && navTabs.length >= 4;
    const pendingFromLogin =
      prevCount === null &&
      navTabs.length >= 4 &&
      sessionStorage.getItem(KIOSK_NAV_APPEAR_SESSION_KEY) === '1';

    if ((expanded || justAuthed || pendingFromLogin) && !loginAppearDoneRef.current) {
      loginAppearDoneRef.current = true;
      triggerNavAppear();
    }

    prevAuthedRef.current = isAuthenticated;
    prevNavTabCountRef.current = navTabs.length;
  }, [isAuthenticated, navTabs.length, triggerNavAppear]);

  return (
    <header
      className={cx(
        // Prototype static chrome: brand + tabs + actions — no back / breadcrumb
        // overflow-x only: language / account menus must escape vertically
        'w-full overflow-x-clip overflow-y-visible',
        compact
          ? 'relative z-50 min-h-[var(--wf-header-h,56px)] bg-space-top/95 backdrop-blur-md'
          : 'sticky top-0 z-40 min-h-[calc(var(--proto-hd-action-h)+2*var(--proto-hd-pad-y))] bg-space-top/85 backdrop-blur-md',
        'border-b border-border-ice',
        'flex items-center',
        className
      )}
      style={{
        paddingLeft: 'var(--proto-hd-pad-x, 18px)',
        paddingRight: 'var(--proto-hd-pad-x, 18px)',
        paddingTop: compact ? undefined : 'var(--proto-hd-pad-y, 12px)',
        paddingBottom: compact ? undefined : 'var(--proto-hd-pad-y, 12px)',
        ...style,
      }}
      {...restProps}
    >
      <div
        className="w-full flex items-center min-w-0 overflow-x-clip overflow-y-visible"
        style={{ gap: 'var(--proto-hd-gap, 16px)' }}
      >
        <button
          type="button"
          onClick={() => {
            playClickSound();
            navigate('/');
          }}
          className="flex flex-shrink-0 items-center gap-2 focus-visible:outline-none focus-visible:shadow-focus-ring rounded-control-sm"
          aria-label={t('header.goHome', 'Go to home')}
        >
          <span
            className="flex items-center justify-center rounded-[7px] flex-shrink-0 bg-gradient-to-br from-money-in to-violet-brand shadow-glow-magenta font-display font-extrabold italic text-white leading-none"
            style={{
              width: 'var(--proto-hd-logo)',
              height: 'var(--proto-hd-logo)',
              fontSize: 'calc(var(--proto-hd-logo) * 0.55)',
            }}
            aria-hidden
          >
            K
          </span>
          <span
            className="hd-brand-text font-display font-bold tracking-tight text-ink whitespace-nowrap leading-none"
            style={wfBrandStyle}
          >
            {t('header.appName', 'Kiosk Gaming')}
          </span>
        </button>

        {/* Nav sits immediately after brand (left-aligned, not centered) */}
        <TopTabs
          tabs={navTabs}
          activeId={activeTabId}
          onChange={handleTabChange}
          waveGeneration={navAppearGen}
          onAppearComplete={handleNavAppearComplete}
          className="flex-shrink-0"
        />

        {/* Spacer → right actions (Prototype margin-left:auto) */}
        <div className="flex-1 min-w-0" aria-hidden />

        <div
          className="flex min-w-0 flex-shrink items-center overflow-x-clip overflow-y-visible"
          style={{ gap: 'var(--proto-chip-gap)' }}
        >
          {authedUi && rightSlot == null && (
            <SignupBonusBadge compact={compact} />
          )}

          {showBalanceUi && rightSlot == null && (
            <div
              data-tour-id={showBalanceUi ? 'post-login-tour.balance' : undefined}
              className={cx(
                'relative flex-shrink-0 transition-transform duration-300',
                showBalanceAttention ? 'scale-[1.05]' : 'scale-100'
              )}
            >
              {balanceDelta !== null && (
                <span
                  className={cx(
                    'absolute -top-2 -right-2 z-10 px-1.5 py-0.5 rounded-md font-money font-bold border tabular-nums',
                    fsClass.base,
                    balanceDelta > 0
                      ? 'text-success border-success/50 bg-success/20'
                      : 'text-danger-soft border-danger/50 bg-danger/20',
                    showBalanceAttention && 'animate-pulse'
                  )}
                >
                  {balanceDelta > 0 ? '+' : '-'}${Math.abs(balanceDelta).toFixed(2)}
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  playClickSound();
                  requireAuth(() => navigate('/account/finance/wallet'));
                }}
                onDoubleClick={handleReloadBalance}
                disabled={balanceLoading}
                aria-label={t('header.balance', 'Wallet balance')}
                className={cx(
                  'hd-chip hd-chip-gold group',
                  showBalanceAttention && 'shadow-glow-gold',
                  'transition-all duration-150',
                  'disabled:opacity-70'
                )}
              >
                {balanceLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-money-out-soft" />
                ) : (
                  <span className="font-money font-semibold text-money-out-soft tabular-nums whitespace-nowrap">
                    ${animatedBalance.toFixed(2)}
                  </span>
                )}
              </button>
            </div>
          )}

          {authedUi && (
            <Button
              variant="moneyIn"
              size="md"
              className={cx(
                'hd-btn flex-shrink-0',
                // Mobile: square wallet+ badge — label too wide for chrome row
                'w-[var(--proto-hd-chip-h)] !gap-0 !px-0 sm:w-auto sm:!px-[var(--wf-chip-px)]'
              )}
              data-tour-id="post-login-tour.deposit"
              aria-label={t('header.addFunds', 'Add funds')}
              onClick={() => {
                playClickSound();
                requireAuth(() =>
                  navigate('/account/finance/wallet/deposit', navigationFromState(location.pathname))
                );
              }}
            >
              <span className="relative inline-flex sm:hidden" aria-hidden>
                <Wallet className="h-[18px] w-[18px]" strokeWidth={2.25} />
                <span className="absolute -right-2 -top-1.5 flex size-3.5 items-center justify-center rounded-full bg-white shadow-sm">
                  <Plus className="size-2.5 text-money-in-deep" strokeWidth={3} />
                </span>
              </span>
              <span className="hidden sm:inline">{t('header.addFunds', '+ Add funds')}</span>
            </Button>
          )}

          {showBell ? (
            <div data-tour-id="post-login-tour.notification">
              <KioskNotificationBell compact={compact} />
            </div>
          ) : null}

          <LanguageSwitcher compact={compact} />

          {type === 'logged' && rightSlot != null && rightSlot}

          {type === 'normal' && !isAuthenticated && (
            <button
              type="button"
              onClick={() => {
                playClickSound();
                openAuthModal('signin');
              }}
              className="btn-shine hd-btn flex flex-shrink-0 items-center justify-center gap-2 hover:opacity-90 transition-all duration-200 active:scale-95 !font-bold !text-[length:var(--wf-hd-action)]"
              style={{
                ...guestAuthShineStyle,
                width: 'auto',
                maxWidth: '226px',
                borderRadius: '12px',
                border: '2px solid #10B981',
                background: 'rgba(16, 185, 129, 0.10)',
                boxShadow:
                  '0 0 20px 0 rgba(16, 185, 129, 0.40), 0 0 15px 2px rgba(16, 185, 129, 0.20) inset',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                color: '#fff',
                letterSpacing: '0.06em',
              }}
              aria-label={t('header.openMyWallet', 'Sign in / Register')}
            >
              <img
                src="/icons/wallet-icon.svg"
                alt=""
                aria-hidden
                className="flex-shrink-0 w-4 h-5 sm:w-5 sm:h-6"
                style={{ objectFit: 'contain' }}
              />
              <span className="whitespace-nowrap leading-tight">
                {t('header.openMyWallet', 'Sign in / Register')}
              </span>
            </button>
          )}

          {/* Account avatar → Account & settings / Support / FAQ / Log out */}
          {authedUi && showUserInfo && (
            <div ref={menuRef} className="relative flex-shrink-0">
              <button
                ref={avatarButtonRef}
                type="button"
                onClick={() => {
                  playClickSound();
                  setMenuOpen((open) => !open);
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={t('header.accountMenu', 'Account menu')}
                data-tour-id="post-login-tour.settings"
                className="hd-avatar shrink-0"
              >
                <span className="hd-avatar-inner" aria-hidden>
                  <UserRound className="h-4 w-4" strokeWidth={2.2} />
                </span>
              </button>

              {menuOpen && menuLayout
                ? createPortal(
                    <div
                      ref={menuPanelRef}
                      role="menu"
                      className="cosmos-dropdown cosmos-pop-in py-2 shadow-card"
                      style={{
                        position: 'fixed',
                        top: menuLayout.top,
                        left: menuLayout.left,
                        minWidth: menuLayout.minWidth,
                        zIndex: 1080,
                      }}
                    >
                  {(
                    [
                      {
                        icon: UserRound,
                        label: t('header.menu.accountSettings', 'Account & settings'),
                        path: '/account/settings',
                        tourId: 'post-login-tour.settings',
                      },
                      {
                        icon: Shield,
                        label: t('header.menu.accountSecurity', 'Account security'),
                        path: '/account/security',
                      },
                      {
                        icon: Zap,
                        label: t('header.menu.animations', 'Animations & sound'),
                        path: '/account/animations',
                      },
                      {
                        icon: LifeBuoy,
                        label: t('header.menu.support', 'Support'),
                        path: '/support',
                      },
                      {
                        icon: CircleHelp,
                        label: t('header.menu.faq', 'FAQ'),
                        path: '/faq',
                      },
                    ] as const
                  ).map(({ icon: Icon, label, path, ...rest }) => (
                    <button
                      key={path}
                      type="button"
                      role="menuitem"
                      data-tour-id={'tourId' in rest ? rest.tourId : undefined}
                      onClick={() => handleMenuNavigate(path)}
                      className="w-full flex items-center gap-3 min-h-[44px] px-4 font-ui text-[length:var(--wf-hd-action)] text-body-muted hover:text-ink hover:bg-white/5 transition-colors"
                    >
                      <Icon className="w-4 h-4 text-ice/70 shrink-0" />
                      {label}
                    </button>
                  ))}

                  <div className="my-1.5 mx-3 border-t border-dashed border-border-ice" aria-hidden />
                  <button
                    type="button"
                    role="menuitem"
                    data-tour-id="post-login-tour.logout"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 min-h-[44px] px-4 font-ui text-[length:var(--wf-hd-action)] text-danger-soft hover:bg-danger/15 transition-colors"
                  >
                    <LogOut className="w-4 h-4 shrink-0" />
                    {t('header.menu.logout', 'Log out')}
                  </button>
                    </div>,
                    document.body
                  )
                : null}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
